<template>
  <!-- MYAC-1269 — sharing, from the conversation's own header. The owner shares
       or manages; a participant sees that it is shared with them, who else is on
       it, and the way out. One control, on the shared chat, so the docked panel
       and the full page get it alike. -->
  <template v-if="right === 'owner' || right === null">
    <!-- A conversation with no turn yet has nothing to share: the control stays,
         disabled with its reason (never hidden on a temporary state). -->
    <span v-tooltip.bottom="shareTooltip" class="inline-flex shrink-0">
      <Button :icon="shared ? 'pi pi-users' : 'pi pi-user-plus'" :label="compact ? undefined : shareLabel"
        text size="small" :aria-label="shareLabel" :disabled="right !== 'owner'"
        data-testid="conversation-share" @click="open = true" />
    </span>
    <ShareConversationDialog :visible="open" @close="open = false" />
  </template>
  <template v-else>
    <span v-tooltip.bottom="whoLine" class="inline-flex shrink-0">
      <Tag :value="t('conversations.share.shared_with_you_tag')" severity="secondary" icon="pi pi-users"
        data-testid="conversation-shared-tag" />
    </span>
    <span v-if="named" v-tooltip.bottom="t('conversations.leave')" class="inline-flex shrink-0">
      <Button icon="pi pi-sign-out" text size="small" :aria-label="t('conversations.leave')"
        :disabled="busy" data-testid="conversation-leave" @click="leave" />
    </span>
  </template>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import { useChatbotStore } from '@/stores/chatbot'
import { useConversationsStore } from '@/stores/conversations'
import { useMembersStore } from '@/stores/members'
import { useProjectsStore } from '@/stores/projects'
import { useUsersStore } from '@/stores/users'
import { memberName } from '@/utils/userDisplay'
import { startNewConversation } from '@/composables/conversationMoves'
import ShareConversationDialog from './ShareConversationDialog.vue'

withDefaults(defineProps<{ compact?: boolean }>(), { compact: false })

const { t } = useI18n()
const chatbot = useChatbotStore()
const conversations = useConversationsStore()
const members = useMembersStore()
const users = useUsersStore()

const open = ref(false)
const projects = useProjectsStore()
const projectName = computed(() => projects.projects.find(p => p.id === chatbot.projectId)?.name ?? '')
const right = computed(() => chatbot.conversationRight)
const row = computed(() => chatbot.chatRow)
const me = computed(() => users.user?.uid ?? '')
const shared = computed(() => (row.value?.visibility ?? 'private') !== 'private')
const named = computed(() => !!me.value && (row.value?.participant_uids ?? []).includes(me.value))
const busy = computed(() => !!chatbot.sessionId && conversations.sharing[chatbot.sessionId] === true)

const shareLabel = computed(() => t(shared.value ? 'conversations.share.manage' : 'conversations.share.button'))
const shareTooltip = computed(() => right.value === 'owner' ? shareLabel.value : t('conversations.share.not_yet'))

// the names of who is on it, for the participant's hover (the owner's dialog lists them)
watch([() => chatbot.projectId, right], ([pid, r]) => {
  if (pid && r && r !== 'owner') void members.listProjectMembers(pid)
}, { immediate: true })
function nameOf(uid: string): string {
  return memberName(members.projectMembers.find(m => m.uid === uid)) || t('shared.unknown_user')
}
const whoLine = computed(() => {
  const r = row.value
  if (!r) return ''
  const label = (uid: string, role: 'owner' | 'read' | 'write') =>
    uid === me.value ? t(`conversations.share.who.${role}_you`) : t(`conversations.share.who.${role}`, { name: nameOf(uid) })
  const parts: string[] = []
  if (r.owner_uid) parts.push(label(r.owner_uid, 'owner'))
  for (const uid of r.participant_uids ?? []) parts.push(label(uid, (r.writer_uids ?? []).includes(uid) ? 'write' : 'read'))
  if (r.visibility === 'project') {
    parts.push(t(`conversations.share.who.project_${r.project_right === 'write' ? 'write' : 'read'}`, { project: projectName.value }))
  }
  return t('conversations.share.who.line', { people: parts.join(', ') })
})

async function leave(): Promise<void> {
  if (!chatbot.projectId || !chatbot.sessionId) return
  const left = await conversations.leave(chatbot.projectId, chatbot.sessionId)
  // A person who left can no longer read the row: the listeners would only log
  // their refusal and keep the stale transcript on screen. Leave the
  // conversation too — a fresh one, as the other moves do.
  if (left) await startNewConversation()
}

// MYAC-1269 — « Partager… » from a row of the list: once that conversation is the
// one on screen and the person owns it, open the share window here.
// A request only counts for a few seconds: one the conversation never answered
// (the person was not its owner after all) must not open the window later.
const SHARE_REQUEST_TTL_MS = 10_000
watch(() => [conversations.shareRequest, chatbot.sessionId, right.value] as const, ([req, sid, r]) => {
  if (!req || req.chatId !== sid) return
  if (Date.now() - req.at > SHARE_REQUEST_TTL_MS) { conversations.shareRequest = null; return }
  if (r !== 'owner') return
  conversations.shareRequest = null
  open.value = true
}, { immediate: true })
</script>
